import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, GripVertical, Users, CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { title: "", description: "", priority: "medium", status: "todo", due_date: "" };

function dueMeta(due, done) {
  if (!due) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(due + "T00:00:00");
  const days = Math.round((d - today) / 86400000);
  const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (done) return { label, cls: "text-muted-foreground border-border" };
  if (days < 0) return { label: `${label} · overdue`, cls: "text-rose-500 border-rose-500/40" };
  if (days === 0) return { label: `${label} · today`, cls: "text-amber-500 border-amber-500/40" };
  if (days <= 3) return { label: `${label} · ${days}d`, cls: "text-amber-500 border-amber-500/40" };
  return { label, cls: "text-muted-foreground border-border" };
}

const COLUMNS = [
  { id: "todo", label: "To Do", color: "bg-slate-400" },
  { id: "in_progress", label: "In Progress", color: "bg-blue-500" },
  { id: "done", label: "Done", color: "bg-accent" },
];
const PRIORITY = { high: "text-rose-500", medium: "text-amber-500", low: "text-muted-foreground" };

export default function Tasks() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["tasks"], queryFn: async () => (await api.get("/tasks")).data });

  const openAdd = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditId(t.id); setForm({ title: t.title || "", description: t.description || "", priority: t.priority || "medium", status: t.status || "todo", due_date: t.due_date || "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, due_date: form.due_date || null };
      if (editId) { await api.put(`/tasks/${editId}`, payload); toast.success("Task updated"); }
      else { await api.post("/tasks", payload); toast.success("Task created"); }
      setOpen(false); setEditId(null); setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const move = async (id, status) => {
    try {
      await api.patch(`/tasks/${id}/status`, { status });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (id) => { await api.delete(`/tasks/${id}`); qc.invalidateQueries({ queryKey: ["tasks"] }); };

  return (
    <div>
      <PageHeader title="Tasks" subtitle="Keep projects and to-dos moving." testId="tasks-header" />
      {isLoading ? <PageLoader /> : (
        <div>
          <div className="mb-5 flex justify-end">
            <Button onClick={openAdd} data-testid="add-task-button"><Plus className="mr-2 h-4 w-4" /> New task</Button>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {COLUMNS.map((col) => {
              const items = tasks.filter((t) => t.status === col.id);
              return (
                <div key={col.id} className="rounded-2xl border border-border bg-secondary/30 p-3"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { const id = e.dataTransfer.getData("id"); if (id) move(id, col.id); }}
                  data-testid={`kanban-column-${col.id}`}>
                  <div className="mb-3 flex items-center justify-between px-1">
                    <div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${col.color}`} /><span className="text-sm font-semibold">{col.label}</span></div>
                    <span className="rounded-full bg-background px-2 text-xs text-muted-foreground">{items.length}</span>
                  </div>
                  <div className="space-y-2.5 min-h-[80px]">
                    {items.map((t) => (
                      <div key={t.id} draggable onDragStart={(e) => e.dataTransfer.setData("id", t.id)}
                        className="group cursor-grab rounded-xl border border-border bg-card p-3 active:cursor-grabbing" data-testid={`task-card-${t.id}`}>
                        <div className="flex items-start gap-2">
                          <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{t.title}</p>
                            {t.contact_name && <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-accent"><Users className="h-3 w-3" /> {t.contact_name}</p>}
                            {t.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{t.description}</p>}
                            {(() => { const dm = dueMeta(t.due_date, t.status === "done"); return dm ? <span className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${dm.cls}`} data-testid={`task-due-${t.id}`}><CalendarDays className="h-3 w-3" /> {dm.label}</span> : null; })()}
                            <div className="mt-2 flex items-center justify-between">
                              <span className={`text-xs font-semibold capitalize ${PRIORITY[t.priority]}`}>{t.priority}</span>
                              <div className="flex items-center gap-1">
                                <Select value={t.status} onValueChange={(v) => move(t.id, v)}>
                                  <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{COLUMNS.map((c) => <SelectItem key={c.id} value={c.id} className="text-xs">{c.label}</SelectItem>)}</SelectContent>
                                </Select>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(t.id)} data-testid={`delete-task-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">Drop tasks here</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(EMPTY); } }}>
        <DialogContent data-testid="task-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="task-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "medium", "high"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Column</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="task-column-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{COLUMNS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Due date <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <div className="flex items-center gap-2">
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-date-input" />
                {form.due_date && <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, due_date: "" })} data-testid="task-due-date-clear">Clear</Button>}
              </div>
              <p className="flex items-center gap-1 text-xs text-muted-foreground"><CalendarDays className="h-3 w-3" /> Tasks with a due date show up on your Calendar. Leave blank for a deadline-free task.</p>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.title} data-testid="save-task-button">{editId ? "Save changes" : "Create task"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
